import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { 
  GraduationCap, 
  Users, 
  Brain, 
  Target, 
  TrendingUp, 
  CheckCircle, 
  AlertTriangle, 
  XCircle,
  Database,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  Shield,
  BookOpen,
  Search,
  FileText,
  Globe,
  Calendar,
  HelpCircle,
  Info
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function AITraining() {
  const { toast } = useToast();
  
  // State
  const [actors, setActors] = useState([]);
  const [selectedActor, setSelectedActor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Dashboard state
  const [dashboard, setDashboard] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  
  // Testing state
  const [testQuery, setTestQuery] = useState("");
  const [testResults, setTestResults] = useState(null);
  const [testingKnowledge, setTestingKnowledge] = useState(false);
  
  // Validation state
  const [validationResults, setValidationResults] = useState(null);
  const [runningValidation, setRunningValidation] = useState(false);
  
  // History state
  const [history, setHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyDays, setHistoryDays] = useState(30);
  
  // Predefined test questions
  const [predefinedTests, setPredefinedTests] = useState([
    { question: "What are your pricing packages?", category: "pricing" },
    { question: "How can I contact support?", category: "support" },
    { question: "What services do you offer?", category: "services" }
  ]);
  const [autoTestResults, setAutoTestResults] = useState(null);
  const [runningAutoTests, setRunningAutoTests] = useState(false);
  
  // Guide modal state
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    fetchActors();
  }, []);

  useEffect(() => {
    if (selectedActor) {
      fetchDashboard();
    }
  }, [selectedActor]);

  const fetchActors = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/ai/actors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActors(response.data);
      if (response.data.length > 0) {
        setSelectedActor(response.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching actors:", error);
      toast({ title: "Error", description: "Failed to fetch AI actors", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    if (!selectedActor) return;
    setLoadingDashboard(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/admin/ai-training/actor/${selectedActor}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log("AI Training Dashboard Response:", response.data);
      console.log("Knowledge Bases:", response.data.knowledge_bases);
      console.log("Total Linked:", response.data.knowledge_bases?.total_linked);
      setDashboard(response.data);
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      toast({ title: "Error", description: "Failed to fetch dashboard", variant: "destructive" });
    } finally {
      setLoadingDashboard(false);
    }
  };

  const handleTestKnowledge = async () => {
    if (!testQuery.trim()) {
      toast({ title: "Error", description: "Please enter a test query", variant: "destructive" });
      return;
    }
    
    setTestingKnowledge(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/api/admin/ai-training/actor/${selectedActor}/test-knowledge?test_query=${encodeURIComponent(testQuery)}`,
        {},
        {
          headers: { 
            Authorization: `Bearer ${token}`
          }
        }
      );
      console.log("Test Knowledge Response:", response.data);
      setTestResults(response.data);
      toast({ title: "Success", description: "Knowledge test completed", variant: "default" });
    } catch (error) {
      console.error("Test Knowledge Error:", error);
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to test knowledge", variant: "destructive" });
    } finally {
      setTestingKnowledge(false);
    }
  };

  const handleRunValidation = async () => {
    setRunningValidation(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/api/admin/ai-training/actor/${selectedActor}/run-validation`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setValidationResults(response.data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to run validation", variant: "destructive" });
    } finally {
      setRunningValidation(false);
    }
  };
  
  const handleRunAutomatedTests = async () => {
    setRunningAutoTests(true);
    const results = [];
    
    for (const test of predefinedTests) {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.post(
          `${API}/api/admin/ai-training/actor/${selectedActor}/test-knowledge`,
          test.question,
          {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'text/plain'
            }
          }
        );
        
        // Score based on if knowledge was found
        const score = response.data.total_kb_matches > 0 ? "pass" : "fail";
        results.push({
          question: test.question,
          category: test.category,
          score: score,
          matches: response.data.total_kb_matches,
          coaching_notes: response.data.coaching_notes_matched.length
        });
      } catch (error) {
        results.push({
          question: test.question,
          category: test.category,
          score: "error",
          matches: 0,
          coaching_notes: 0
        });
      }
    }
    
    setAutoTestResults(results);
    setRunningAutoTests(false);
    
    const passedTests = results.filter(r => r.score === "pass").length;
    toast({ 
      title: "Automated Tests Complete", 
      description: `${passedTests}/${results.length} tests passed` 
    });
  };

  const fetchHistory = async () => {
    if (!selectedActor) return;
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/api/admin/ai-training/actor/${selectedActor}/history?days=${historyDays}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setHistory(response.data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to fetch history", variant: "destructive" });
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (activeTab === "history" && selectedActor) {
      fetchHistory();
    }
  }, [activeTab, selectedActor, historyDays]);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (actors.length === 0) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="pt-20 pb-20 text-center">
            <GraduationCap className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h2 className="text-xl font-semibold mb-2">No AI Actors Found</h2>
            <p className="text-gray-600">Create an AI Actor first to access training features.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">AI Training & Validation</h1>
          <p className="text-slate-600">Verify and validate your AI Actor's training with complete transparency</p>
        </div>
        <Button onClick={() => setShowGuide(true)} variant="outline" className="flex items-center gap-2">
          <HelpCircle className="w-4 h-4" />
          View Guide
        </Button>
      </div>

      {/* Actor Selector */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Users className="w-5 h-5 text-blue-600" />
            <div className="flex-1">
              <Label>Select AI Actor</Label>
              <Select value={selectedActor} onValueChange={setSelectedActor}>
                <SelectTrigger className="w-full max-w-md">
                  <SelectValue placeholder="Choose an AI Actor" />
                </SelectTrigger>
                <SelectContent>
                  {actors.map((actor) => (
                    <SelectItem key={actor.id} value={actor.id}>
                      {actor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content - Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="dashboard">
            <Target className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="test">
            <Brain className="w-4 h-4 mr-2" />
            Test
          </TabsTrigger>
          <TabsTrigger value="validation">
            <CheckCircle className="w-4 h-4 mr-2" />
            Validation
          </TabsTrigger>
          <TabsTrigger value="association">
            <Database className="w-4 h-4 mr-2" />
            Association
          </TabsTrigger>
          <TabsTrigger value="history">
            <TrendingUp className="w-4 h-4 mr-2" />
            History
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {loadingDashboard ? (
            <Card>
              <CardContent className="pt-20 pb-20 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              </CardContent>
            </Card>
          ) : dashboard ? (
            <>
              {/* Training Score */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-blue-600" />
                    Training Readiness Score
                  </CardTitle>
                  <CardDescription className="flex items-center gap-2">
                    <Calendar className="w-3 h-3" />
                    Last updated: {dashboard.last_updated ? new Date(dashboard.last_updated).toLocaleString() : 'Never'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="relative pt-1">
                        <div className="flex mb-2 items-center justify-between">
                          <div>
                            <span className="text-5xl font-bold text-blue-600">{dashboard.training_score}</span>
                            <span className="text-2xl text-gray-500">/100</span>
                          </div>
                        </div>
                        <div className="overflow-hidden h-4 mb-4 text-xs flex rounded-full bg-blue-200">
                          <div
                            style={{ width: `${dashboard.training_score}%` }}
                            className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-600 transition-all duration-500"
                          ></div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">{dashboard.knowledge_bases.total_items}</div>
                          <div className="text-xs text-gray-600">Knowledge Items</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-600">{dashboard.coaching_notes.total}</div>
                          <div className="text-xs text-gray-600">Coaching Notes</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-amber-600">{dashboard.feedback.total}</div>
                          <div className="text-xs text-gray-600">Feedback Entries</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Stats Grid - Split into manageable cards */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Knowledge Bases Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Database className="w-5 h-5 text-green-600" />
                      Knowledge Bases
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Linked KBs:</span>
                        <Badge variant="outline">{dashboard.knowledge_bases.total_linked}</Badge>
                      </div>
                      {dashboard.knowledge_bases.details.map((kb) => (
                        <div key={kb.id} className="flex justify-between items-center bg-slate-50 p-2 rounded">
                          <span className="text-sm font-medium">{kb.name}</span>
                          <Badge>{kb.item_count} items</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Coaching Notes Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-purple-600" />
                      Coaching Notes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(dashboard.coaching_notes.by_category).map(([category, count]) => (
                        <div key={category} className="flex justify-between items-center">
                          <span className="text-sm capitalize">{category}</span>
                          <Badge variant="secondary">{count}</Badge>
                        </div>
                      ))}
                      {Object.keys(dashboard.coaching_notes.by_category).length === 0 && (
                        <p className="text-sm text-gray-500">No coaching notes yet</p>
                      )}
                      {dashboard.coaching_notes.from_feedback > 0 && (
                        <div className="pt-2 border-t">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <MessageSquare className="w-3 h-3" />
                              <span>From Feedback</span>
                            </div>
                            <Badge variant="outline" className="bg-purple-50">{dashboard.coaching_notes.from_feedback}</Badge>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Feedback Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-amber-600" />
                      User Feedback
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <ThumbsUp className="w-4 h-4 text-green-600" />
                          <span className="text-sm">Positive</span>
                        </div>
                        <Badge className="bg-green-100 text-green-700">{dashboard.feedback.positive}</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <ThumbsDown className="w-4 h-4 text-red-600" />
                          <span className="text-sm">Negative</span>
                        </div>
                        <Badge className="bg-red-100 text-red-700">{dashboard.feedback.negative}</Badge>
                      </div>
                      <div className="pt-2 border-t">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Total</span>
                          <Badge>{dashboard.feedback.total}</Badge>
                        </div>
                      </div>
                      {dashboard.feedback.converted_to_coaching > 0 && (
                        <div className="pt-2 border-t">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                              <BookOpen className="w-4 h-4 text-purple-600" />
                              <span className="text-sm">Converted to Coaching</span>
                            </div>
                            <Badge className="bg-purple-100 text-purple-700">{dashboard.feedback.converted_to_coaching}</Badge>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Configuration Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-600" />
                      Configuration
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Guard Rails</span>
                        <Badge variant={dashboard.guard_rails.enabled ? "default" : "secondary"}>
                          {dashboard.guard_rails.enabled ? "Enabled" : "Disabled"}
                        </Badge>
                      </div>
                      {dashboard.guard_rails.enabled && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Strictness</span>
                          <Badge variant="outline">{dashboard.guard_rails.strictness}</Badge>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-2 border-t">
                        <span className="text-sm">Total Conversations</span>
                        <Badge>{dashboard.conversations.total}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : null}
        </TabsContent>

        {/* Test Knowledge Tab */}
        <TabsContent value="test" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-blue-600" />
                Test Knowledge Retrieval
              </CardTitle>
              <CardDescription>
                Ask a question to see which knowledge sources your AI will use
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Test Query</Label>
                <Textarea
                  value={testQuery}
                  onChange={(e) => setTestQuery(e.target.value)}
                  placeholder="Example: 'What are your pricing packages?'"
                  rows={3}
                />
              </div>
              <Button 
                onClick={handleTestKnowledge} 
                disabled={testingKnowledge || !testQuery.trim()}
                className="w-full"
              >
                {testingKnowledge ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Testing...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Test Knowledge
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {testResults && (
            <>
              {/* Guard Rails Status - Prominent Display */}
              <Card className={testResults.guard_rail_active ? "border-blue-500 bg-blue-50" : "border-gray-200 bg-gray-50"}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Shield className={`w-6 h-6 ${testResults.guard_rail_active ? 'text-blue-600' : 'text-gray-400'}`} />
                      <div>
                        <h4 className="font-semibold text-base">Guard Rails Status</h4>
                        <p className="text-sm text-gray-600">
                          {testResults.guard_rail_active 
                            ? "Active - AI responses are monitored for on-topic content" 
                            : "Inactive - AI can respond to any query"}
                        </p>
                      </div>
                    </div>
                    <Badge variant={testResults.guard_rail_active ? "default" : "secondary"} className="text-base px-4 py-2">
                      {testResults.guard_rail_active ? "✓ Active" : "○ Inactive"}
                    </Badge>
                  </div>
                  {testResults.guard_rail_active && dashboard && (
                    <div className="mt-3 p-2 bg-blue-100 rounded text-sm text-blue-800">
                      <strong>Strictness Level:</strong> {dashboard.guard_rails.strictness} - Responses will be filtered for relevance
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* KB Results */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Database className="w-5 h-5 text-green-600" />
                      Knowledge Base Matches
                    </div>
                    <Badge>{testResults.knowledge_base_results.length} of {testResults.total_kb_matches}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {testResults.knowledge_base_results.length > 0 ? (
                    <div className="space-y-4">
                      {testResults.knowledge_base_results.map((result, idx) => (
                        <div key={idx} className="border rounded-lg p-4 bg-slate-50">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2 flex-1">
                              {result.source_type === 'document' && <FileText className="w-4 h-4 text-blue-600" />}
                              {result.source_type === 'website' && <Globe className="w-4 h-4 text-green-600" />}
                              {result.source_type === 'manual' && <BookOpen className="w-4 h-4 text-purple-600" />}
                              <span className="text-sm font-medium truncate">{result.source_name}</span>
                            </div>
                            <Badge variant="outline">
                              {(result.similarity_score * 100).toFixed(0)}% match
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-700 bg-white p-2 rounded border">
                            {result.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-8">
                      No knowledge base matches found
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Coaching Notes */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-purple-600" />
                    Coaching Notes ({testResults.coaching_notes_matched.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {testResults.coaching_notes_matched.length > 0 ? (
                    <div className="space-y-3">
                      {testResults.coaching_notes_matched.map((note, idx) => (
                        <div key={idx} className="border rounded-lg p-3 bg-purple-50">
                          <Badge variant="outline" className="mb-2">{note.category}</Badge>
                          <p className="text-sm text-gray-700">{note.content}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-8">
                      No coaching notes matched
                    </p>
                  )}
                </CardContent>
              </Card>
              {/* Debug Information - What Fuels the AI */}
              {testResults.debug_info && (
                <Card className="border-2 border-yellow-200 bg-yellow-50">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-600" />
                      🔍 Debug: What Fuels the AI Response
                      <Badge variant="destructive" className="ml-auto">Advanced</Badge>
                    </CardTitle>
                    <CardDescription>
                      See exactly what information the AI receives and in what priority order
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Priority Order */}
                    <div className="bg-white rounded-lg p-4 border-2 border-yellow-300">
                      <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                        📊 Information Priority Order (What AI Uses First)
                      </h4>
                      <div className="space-y-2 text-xs">
                        <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded">
                          <span className="font-bold text-red-700">Priority 1:</span>
                          <span>Knowledge Base (Top 3 semantic matches)</span>
                        </div>
                        <div className="flex items-center gap-2 p-2 bg-purple-50 border border-purple-200 rounded">
                          <span className="font-bold text-purple-700">Priority 2:</span>
                          <span>Coaching Notes (Admin guidance)</span>
                        </div>
                        <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded">
                          <span className="font-bold text-blue-700">Priority 3:</span>
                          <span>System Prompt (Actor personality & instructions)</span>
                        </div>
                        <div className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded">
                          <span className="font-bold text-gray-700">Priority 4:</span>
                          <span>Actor Profile (Experience, credentials, background)</span>
                        </div>
                        <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded">
                          <span className="font-bold text-green-700">Priority 5:</span>
                          <span>Chat History (Previous messages in conversation)</span>
                        </div>
                      </div>
                    </div>

                    {/* Full System Prompt Preview */}
                    <details className="bg-white rounded-lg p-4 border">
                      <summary className="font-semibold text-sm cursor-pointer hover:text-blue-600">
                        📝 Full System Prompt (Click to expand)
                      </summary>
                      <div className="mt-3 p-3 bg-gray-50 rounded text-xs font-mono whitespace-pre-wrap max-h-96 overflow-y-auto">
                        {testResults.debug_info.full_system_prompt}
                      </div>
                    </details>

                    {/* KB Results Full Content */}
                    {testResults.debug_info.kb_results_full && testResults.debug_info.kb_results_full.length > 0 && (
                      <details className="bg-white rounded-lg p-4 border">
                        <summary className="font-semibold text-sm cursor-pointer hover:text-blue-600">
                          📚 Full KB Content Sent to AI (Click to expand)
                        </summary>
                        <div className="mt-3 space-y-3">
                          {testResults.debug_info.kb_results_full.map((item, idx) => (
                            <div key={idx} className="p-3 bg-green-50 rounded border border-green-200">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-semibold text-sm">{item.source}</span>
                                <Badge variant="outline">{(item.similarity * 100).toFixed(1)}% match</Badge>
                              </div>
                              <div className="text-xs text-gray-700 whitespace-pre-wrap font-mono bg-white p-2 rounded">
                                {item.full_content}
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* AI Response Preview */}
              <Card className="border-2 border-blue-200 bg-blue-50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                      AI Response Preview
                    </div>
                    <div className="flex items-center gap-2">
                      {testResults.model_used && (
                        <Badge variant="secondary" className="text-xs">
                          Model: {testResults.model_used}
                        </Badge>
                      )}
                      <Badge variant="outline">What Users Will See</Badge>
                    </div>
                  </CardTitle>
                  <CardDescription>
                    Using actor's configured LLM model (with fallback support if primary fails)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {testResults.ai_response ? (
                    <div className="bg-white rounded-lg p-4 border-2 border-blue-300">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-lg">🤖</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                            {testResults.ai_response}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : testResults.ai_response_error ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-yellow-900 mb-1">Could not generate AI response</p>
                          <p className="text-xs text-yellow-800">{testResults.ai_response_error}</p>
                          <p className="text-xs text-yellow-700 mt-2">The knowledge retrieval works, but the AI couldn't generate a response. Check your OpenRouter API key in Settings.</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                      <p className="text-sm text-gray-600">Generating AI response...</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Validation Tab */}
        <TabsContent value="validation" className="space-y-6">
          {/* How to Validate Guide */}
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-blue-600" />
                How to Validate?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <details className="space-y-4">
                <summary className="font-semibold cursor-pointer hover:text-blue-600 flex items-center gap-2 p-3 bg-white rounded border">
                  📖 Click to read the Validation Guide
                </summary>
                
                <div className="mt-4 space-y-6 text-sm">
                  {/* Introduction */}
                  <div className="bg-white p-4 rounded border">
                    <h4 className="font-bold text-base mb-2 text-blue-700">🔍 Configuration Validation</h4>
                    <p className="text-gray-700 mb-2">
                      The Validation tab provides a comprehensive health check to ensure your AI Actor is properly configured and ready for production.
                    </p>
                    <div className="p-3 bg-green-50 rounded border border-green-200 mt-3">
                      <p className="font-semibold text-green-800">⚙️ Automated Configuration Check</p>
                      <p className="text-xs text-gray-600 mt-1">Runs 7 diagnostic tests to verify system setup - like a "health checkup" for your AI</p>
                    </div>
                  </div>

                  {/* Configuration Validation */}
                  <div className="bg-white p-4 rounded border">
                    <h4 className="font-bold text-base mb-3 text-green-700">⚙️ Configuration Validation</h4>
                    <p className="text-gray-700 mb-3">
                      A comprehensive <strong>health check</strong> of your AI Actor's setup - runs 7 automated diagnostic tests.
                    </p>
                    
                    <div className="space-y-2 text-xs">
                      <div className="p-2 bg-gray-50 rounded border">
                        <p className="font-semibold">✅ Test 1: Knowledge Base Connection</p>
                        <p className="text-gray-600">Is at least 1 KB linked? Without KBs, AI can't provide knowledge-based answers.</p>
                      </div>
                      <div className="p-2 bg-gray-50 rounded border">
                        <p className="font-semibold">✅ Test 2: Knowledge Base Content</p>
                        <p className="text-gray-600">Do linked KBs have actual content? Empty KBs = no knowledge to use.</p>
                      </div>
                      <div className="p-2 bg-gray-50 rounded border">
                        <p className="font-semibold">✅ Test 3: Coaching Notes (Smart Memory)</p>
                        <p className="text-gray-600">Are coaching notes configured? They guide AI behavior and responses.</p>
                      </div>
                      <div className="p-2 bg-gray-50 rounded border">
                        <p className="font-semibold">✅ Test 4: User Feedback</p>
                        <p className="text-gray-600">Has AI received feedback? Helps improve over time.</p>
                      </div>
                      <div className="p-2 bg-gray-50 rounded border">
                        <p className="font-semibold">✅ Test 5: Guard Rails</p>
                        <p className="text-gray-600">Are guard rails configured? Prevents off-topic responses.</p>
                      </div>
                      <div className="p-2 bg-gray-50 rounded border">
                        <p className="font-semibold">✅ Test 6: LLM Configuration</p>
                        <p className="text-gray-600">Is primary LLM selected? Can't generate responses without it!</p>
                      </div>
                      <div className="p-2 bg-gray-50 rounded border">
                        <p className="font-semibold">✅ Test 7: Fallback Models</p>
                        <p className="text-gray-600">Are backup models configured? Provides reliability if primary fails.</p>
                      </div>
                    </div>

                    <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
                      <p className="font-semibold text-xs text-blue-900">When to Use:</p>
                      <ul className="text-xs text-blue-800 mt-1 list-disc list-inside">
                        <li>Before launching your AI Actor</li>
                        <li>After making configuration changes</li>
                        <li>Monthly maintenance checks</li>
                        <li>When troubleshooting issues</li>
                      </ul>
                    </div>
                  </div>

                  {/* Workflow */}
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded border border-gray-300">
                    <h4 className="font-bold text-base mb-3">🔄 Recommended Workflow</h4>
                    <div className="space-y-2 text-xs">
                      <div className="flex items-start gap-2">
                        <span className="font-bold text-green-700">Step 1:</span>
                        <div>
                          <p className="font-semibold">Run Configuration Validation</p>
                          <p className="text-gray-600">Comprehensive health check - runs 7 diagnostic tests on your AI Actor setup</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-center">
                        <span className="text-2xl">↓</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-bold text-blue-700">Step 2:</span>
                        <div>
                          <p className="font-semibold">Review Results</p>
                          <p className="text-gray-600">Check which tests passed, have warnings, or failed</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-center">
                        <span className="text-2xl">↓</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-bold text-orange-700">Step 3:</span>
                        <div>
                          <p className="font-semibold">Fix Issues</p>
                          <p className="text-gray-600">Address failures and warnings by configuring the missing settings</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-center">
                        <span className="text-2xl">↓</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-bold text-green-700">Step 4:</span>
                        <div>
                          <p className="font-semibold">Test with Real Queries</p>
                          <p className="text-gray-600">Use the "Test" tab to verify AI responses with specific questions</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-center">
                        <span className="text-2xl">↓</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-bold text-purple-700">Step 5:</span>
                        <div>
                          <p className="font-semibold">Ready for Production</p>
                          <p className="text-gray-600">When all tests pass, your AI Actor is configured and ready for users!</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* What Gets Validated */}
                  <div className="bg-white p-4 rounded border">
                    <h4 className="font-bold text-base mb-3">✅ What Configuration Validation Checks</h4>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div className="p-3 bg-blue-50 rounded border">
                        <p className="font-semibold text-blue-900">System Setup</p>
                        <p className="text-gray-600 mt-1">Verifies all required components are configured</p>
                      </div>
                      <div className="p-3 bg-purple-50 rounded border">
                        <p className="font-semibold text-purple-900">Knowledge Availability</p>
                        <p className="text-gray-600 mt-1">Ensures KB and coaching notes exist</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded border">
                        <p className="font-semibold text-green-900">Model Configuration</p>
                        <p className="text-gray-600 mt-1">Confirms LLM and fallback models set</p>
                      </div>
                      <div className="p-3 bg-amber-50 rounded border">
                        <p className="font-semibold text-amber-900">Safety Settings</p>
                        <p className="text-gray-600 mt-1">Validates guard rails and filters</p>
                      </div>
                    </div>
                  </div>

                  {/* Pro Tips */}
                  <div className="bg-yellow-50 p-4 rounded border border-yellow-300">
                    <h4 className="font-bold text-base mb-2 flex items-center gap-2">
                      💡 Pro Tips
                    </h4>
                    <ul className="text-xs space-y-1 list-disc list-inside text-gray-700">
                      <li>Run validation before launching your AI Actor to users</li>
                      <li>Run validation after making any configuration changes</li>
                      <li>Aim for 100% PASS on all 7 configuration tests</li>
                      <li>Warnings are acceptable but should be addressed when possible</li>
                      <li>Use the "Test" tab to verify AI responses with specific queries</li>
                      <li>Check validation monthly as part of regular maintenance</li>
                      <li>All PASS tests = Your AI Actor is production-ready! 🚀</li>
                    </ul>
                  </div>
                </div>
              </details>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Training Validation
              </CardTitle>
              <CardDescription>
                Run automated tests to verify training status
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                onClick={handleRunValidation} 
                disabled={runningValidation}
                className="w-full"
              >
                {runningValidation ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Running Validation...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Run Configuration Validation
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
          
          {validationResults && (
            <>
              {/* Overall Status */}
              <Card className={
                validationResults.overall_status === "pass" 
                  ? "border-green-200 bg-green-50" 
                  : "border-red-200 bg-red-50"
              }>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold mb-1">
                        {validationResults.overall_status === "pass" 
                          ? "✅ Training Validated" 
                          : "❌ Issues Found"}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {validationResults.tests_passed} passed • {validationResults.tests_warning} warnings • {validationResults.tests_failed} failed
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold">
                        {validationResults.tests_passed}/{validationResults.total_tests}
                      </div>
                      <div className="text-xs text-gray-600">Tests Passed</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Test Results */}
              <Card>
                <CardHeader>
                  <CardTitle>Test Results</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {validationResults.validation_results.map((test, idx) => (
                      <div key={idx} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            {test.status === "pass" && <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />}
                            {test.status === "warning" && <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />}
                            {test.status === "fail" && <XCircle className="w-5 h-5 text-red-600 mt-0.5" />}
                            <div className="flex-1">
                              <h4 className="font-medium">{test.test_name}</h4>
                              <p className="text-sm text-gray-600 mt-1">{test.details}</p>
                              {test.recommendation && (
                                <p className="text-sm text-amber-700 bg-amber-50 p-2 rounded mt-2">
                                  💡 {test.recommendation}
                                </p>
                              )}
                            </div>
                          </div>
                          <Badge 
                            variant={
                              test.status === "pass" ? "default" :
                              test.status === "warning" ? "secondary" :
                              "destructive"
                            }
                          >
                            {test.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Association Tab */}
        <TabsContent value="association" className="space-y-6">
          {dashboard && (
            <TooltipProvider>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="w-5 h-5 text-blue-600" />
                    Knowledge Base Associations
                    <Tooltip delayDuration={200}>
                      <TooltipTrigger asChild>
                        <Info className="w-4 h-4 text-blue-500 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm p-4 bg-slate-900 text-white">
                        <div className="space-y-2">
                          <p className="font-semibold text-sm">What are Knowledge Base Associations?</p>
                          <p className="text-xs">Shows which Knowledge Base items are <strong>linked</strong> to your AI Actor. Only associated KBs are available for the AI to reference during conversations.</p>
                          <p className="text-xs mt-2">Think of it as giving specific "textbooks" to your AI - each Actor gets its own curated knowledge set.</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </CardTitle>
                  <CardDescription>
                    Connected knowledge bases for this AI Actor
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {dashboard.knowledge_bases.total_linked > 0 ? (
                    <div className="space-y-4">
                      {dashboard.knowledge_bases.details.map((kb) => (
                        <div key={kb.id} className="border rounded-lg p-4 bg-slate-50">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-lg">{kb.name}</h4>
                            <Badge variant="outline">{kb.item_count} items</Badge>
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="bg-white p-3 rounded border">
                              <div className="text-2xl font-bold text-green-600">{kb.item_count}</div>
                              <div className="text-xs text-gray-600">Knowledge Items</div>
                            </div>
                            <div className="bg-white p-3 rounded border">
                              <CheckCircle className="w-8 h-8 text-blue-600" />
                              <div className="text-xs text-gray-600">Active</div>
                            </div>
                            <div className="bg-white p-3 rounded border">
                              <CheckCircle className="w-8 h-8 text-purple-600" />
                              <div className="text-xs text-gray-600">Embeddings OK</div>
                            </div>
                          </div>
                          <div className="mt-3 p-2 bg-blue-50 rounded text-xs text-blue-700">
                            ✓ Embeddings verified - {kb.item_count} vectors generated for semantic search
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Database className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                      <h3 className="text-lg font-semibold mb-2">No Knowledge Bases</h3>
                      <p className="text-gray-600 mb-4">
                        Link knowledge bases in AI Actors settings
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Embedding Verification Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Embedding Verification
                    <Tooltip delayDuration={200}>
                      <TooltipTrigger asChild>
                        <Info className="w-4 h-4 text-green-500 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-md p-4 bg-slate-900 text-white">
                        <div className="space-y-3">
                          <p className="font-semibold text-sm">What are Embeddings?</p>
                          <p className="text-xs">Embeddings are mathematical representations (vectors) of your knowledge content that enable <strong>semantic search</strong>.</p>
                          
                          <div className="border-t border-gray-600 pt-2">
                            <p className="font-semibold text-sm">How They Connect to AI Knowledge (RAG):</p>
                            <ol className="text-xs space-y-1 mt-1 list-decimal list-inside">
                              <li><strong>Storage:</strong> Knowledge Base content → Converted to embeddings → Stored in database</li>
                              <li><strong>User asks question:</strong> Question → Converted to embedding</li>
                              <li><strong>Search:</strong> System finds similar embeddings (relevant knowledge)</li>
                              <li><strong>Retrieval:</strong> Top matches are injected into AI's context</li>
                              <li><strong>Response:</strong> AI generates answer using retrieved knowledge</li>
                            </ol>
                          </div>
                          
                          <div className="bg-amber-900 p-2 rounded text-xs border border-amber-700">
                            <strong>⚠️ Critical:</strong> Without embeddings, your AI <strong>cannot access</strong> Knowledge Base content, even if it's associated!
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Total Items with Embeddings</span>
                      <Badge className="bg-green-100 text-green-700">{dashboard.knowledge_bases.total_items}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Vector Dimensions</span>
                      <Badge variant="outline">1536 (OpenAI)</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Semantic Search</span>
                      <Badge className="bg-blue-100 text-blue-700">Enabled</Badge>
                    </div>
                    <div className="p-3 bg-green-50 rounded text-sm text-green-800 mt-3">
                      ✅ All knowledge base items have valid embeddings and are ready for AI retrieval
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TooltipProvider>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Training History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Button
                  variant={historyDays === 7 ? "default" : "outline"}
                  size="sm"
                  onClick={() => setHistoryDays(7)}
                >
                  7 Days
                </Button>
                <Button
                  variant={historyDays === 30 ? "default" : "outline"}
                  size="sm"
                  onClick={() => setHistoryDays(30)}
                >
                  30 Days
                </Button>
                <Button
                  variant={historyDays === 90 ? "default" : "outline"}
                  size="sm"
                  onClick={() => setHistoryDays(90)}
                >
                  90 Days
                </Button>
              </div>

              {loadingHistory ? (
                <div className="py-12 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : history ? (
                <div className="space-y-6">
                  {/* Knowledge Additions */}
                  <div>
                    <h4 className="font-semibold mb-3">Knowledge Additions</h4>
                    {history.knowledge_base_additions.length > 0 ? (
                      <div className="space-y-2">
                        {history.knowledge_base_additions.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center bg-slate-50 p-2 rounded">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-500" />
                              <span className="text-sm">{item.date}</span>
                            </div>
                            <Badge>{item.count} items</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No additions in this period</p>
                    )}
                  </div>

                  {/* Feedback */}
                  <div>
                    <h4 className="font-semibold mb-3">Feedback Timeline</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-gray-600 mb-2">Positive</div>
                        {history.feedback_timeline.positive.length > 0 ? (
                          <div className="space-y-1">
                            {history.feedback_timeline.positive.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm bg-green-50 p-2 rounded">
                                <span>{item.date}</span>
                                <Badge className="bg-green-100 text-green-700">{item.count}</Badge>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">None</p>
                        )}
                      </div>
                      <div>
                        <div className="text-sm text-gray-600 mb-2">Negative</div>
                        {history.feedback_timeline.negative.length > 0 ? (
                          <div className="space-y-1">
                            {history.feedback_timeline.negative.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm bg-red-50 p-2 rounded">
                                <span>{item.date}</span>
                                <Badge className="bg-red-100 text-red-700">{item.count}</Badge>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">None</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Conversations */}
                  <div>
                    <h4 className="font-semibold mb-3">Conversations</h4>
                    {history.conversation_timeline.length > 0 ? (
                      <div className="space-y-2">
                        {history.conversation_timeline.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center bg-slate-50 p-2 rounded">
                            <div className="flex items-center gap-2">
                              <MessageSquare className="w-4 h-4 text-gray-500" />
                              <span className="text-sm">{item.date}</span>
                            </div>
                            <Badge>{item.count}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No conversations</p>
                    )}
                  </div>

                  {/* Feedback Conversion Tracking */}
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-purple-600" />
                      Feedback Converted to Coaching
                    </h4>
                    {history.feedback_conversions && history.feedback_conversions.length > 0 ? (
                      <div className="space-y-2">
                        {history.feedback_conversions.map((item, idx) => (
                          <div key={idx} className="border rounded-lg p-3 bg-purple-50">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-500" />
                                <span className="text-sm font-medium">{item.date}</span>
                              </div>
                              <div className="flex gap-2">
                                <Badge variant={item.feedback_type === 'positive' ? 'default' : 'destructive'} className="text-xs">
                                  {item.feedback_type === 'positive' ? <ThumbsUp className="w-3 h-3" /> : <ThumbsDown className="w-3 h-3" />}
                                </Badge>
                                <Badge variant="outline" className="text-xs">{item.category}</Badge>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="text-xs text-gray-700 bg-blue-50 p-2 rounded border border-blue-200">
                                <span className="font-semibold text-blue-900">User:</span> {item.user_message}
                              </div>
                              <div className="text-xs text-gray-700 bg-amber-50 p-2 rounded border border-amber-200">
                                <span className="font-semibold text-amber-900">AI Response:</span> {item.ai_response}
                              </div>
                            </div>
                            <div className="mt-2 flex items-center gap-1 text-xs text-purple-700">
                              <CheckCircle className="w-3 h-3" />
                              <span>Converted to coaching note</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-slate-50 rounded border-2 border-dashed">
                        <BookOpen className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-500">No feedback converted to coaching notes yet</p>
                        <p className="text-xs text-gray-400 mt-1">Use the Feedback Dashboard to convert user feedback into coaching notes</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Success Rate Trends */}
                  <div>
                    <h4 className="font-semibold mb-3">Success Rate Trends</h4>
                    {history.feedback_timeline.positive.length > 0 || history.feedback_timeline.negative.length > 0 ? (
                      <div className="space-y-3">
                        <div className="bg-slate-50 p-4 rounded">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium">Overall Feedback Ratio</span>
                            <Badge className="bg-blue-100 text-blue-700">
                              {(() => {
                                const totalPos = history.feedback_timeline.positive.reduce((sum, item) => sum + item.count, 0);
                                const totalNeg = history.feedback_timeline.negative.reduce((sum, item) => sum + item.count, 0);
                                const total = totalPos + totalNeg;
                                const percentage = total > 0 ? ((totalPos / total) * 100).toFixed(0) : 0;
                                return `${percentage}% Positive`;
                              })()}
                            </Badge>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-green-600 h-2 rounded-full transition-all duration-500"
                              style={{
                                width: `${(() => {
                                  const totalPos = history.feedback_timeline.positive.reduce((sum, item) => sum + item.count, 0);
                                  const totalNeg = history.feedback_timeline.negative.reduce((sum, item) => sum + item.count, 0);
                                  const total = totalPos + totalNeg;
                                  return total > 0 ? ((totalPos / total) * 100) : 0;
                                })()}%`
                              }}
                            ></div>
                          </div>
                          <div className="flex justify-between text-xs text-gray-600 mt-1">
                            <span>{history.feedback_timeline.positive.reduce((sum, item) => sum + item.count, 0)} positive</span>
                            <span>{history.feedback_timeline.negative.reduce((sum, item) => sum + item.count, 0)} negative</span>
                          </div>
                        </div>
                        
                        <div className="text-sm text-gray-700 bg-blue-50 p-3 rounded">
                          <strong>Trend Analysis:</strong>
                          {(() => {
                            const totalPos = history.feedback_timeline.positive.reduce((sum, item) => sum + item.count, 0);
                            const totalNeg = history.feedback_timeline.negative.reduce((sum, item) => sum + item.count, 0);
                            const percentage = totalPos + totalNeg > 0 ? ((totalPos / (totalPos + totalNeg)) * 100) : 0;
                            if (percentage >= 80) return " 🎉 Excellent! Your AI is performing very well.";
                            if (percentage >= 60) return " ✅ Good performance. Continue improving with more training.";
                            if (percentage >= 40) return " ⚠️ Moderate performance. Review negative feedback and add coaching notes.";
                            return " ❌ Needs improvement. Add more knowledge and coaching notes.";
                          })()}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No feedback data for trend analysis</p>
                    )}
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Guide Modal */}
      <Dialog open={showGuide} onOpenChange={setShowGuide}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-blue-600" />
              AI Training Page - Complete Feature Guide
            </DialogTitle>
            <DialogDescription>
              Complete transparency into how your AI Actor is trained and what knowledge it uses
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 text-sm">
            {/* Dashboard Tab */}
            <section>
              <h3 className="text-lg font-bold text-blue-600 mb-3">🎯 Dashboard Tab</h3>
              <div className="space-y-2 text-gray-700">
                <p className="font-semibold">Training Readiness Score (0-100):</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>+30 points: Has knowledge base items</li>
                  <li>+25 points: Has coaching notes</li>
                  <li>+20 points: Has user feedback</li>
                  <li>+15 points: Guard rails enabled</li>
                  <li>+10 points: More than 10 conversations</li>
                </ul>
                <p className="mt-3"><strong>Knowledge Bases:</strong> Linked via "Associated Actors" field when creating/editing a Knowledge Base</p>
                <p><strong>Coaching Notes:</strong> Admin-created notes that guide AI responses (like a cheat sheet)</p>
                <p><strong>Feedback:</strong> Thumbs up/down from users showing satisfaction</p>
              </div>
            </section>

            {/* Test Tab */}
            <section className="border-t pt-4">
              <h3 className="text-lg font-bold text-blue-600 mb-3">🧪 Test Tab - Real-Time Knowledge Testing</h3>
              <div className="space-y-2 text-gray-700">
                <p><strong>What it does:</strong> Test what your AI "knows" WITHOUT actually chatting</p>
                <p className="font-semibold mt-3">How Knowledge Matching Works:</p>
                <ol className="list-decimal pl-6 space-y-1">
                  <li>Your query is converted to an <strong>embedding</strong> (vector representation)</li>
                  <li>System searches all KB items using semantic similarity</li>
                  <li>Results ranked by similarity score (0-100%)</li>
                  <li>80%+ similarity is excellent!</li>
                </ol>
                <div className="bg-blue-50 p-3 rounded mt-3">
                  <p className="font-semibold">💡 Example:</p>
                  <p className="text-xs mt-1">Query: "How much does it cost?"</p>
                  <p className="text-xs">Match: 📄 "Pricing Document" - 95% match</p>
                  <p className="text-xs italic">The AI has excellent pricing knowledge!</p>
                </div>
                <p className="mt-3"><strong>Guard Rails Status:</strong> Shows if on-topic filtering is active (blue = active, gray = inactive)</p>
              </div>
            </section>

            {/* Validation Tab */}
            <section className="border-t pt-4">
              <h3 className="text-lg font-bold text-blue-600 mb-3">✅ Validation Tab - Automated Tests</h3>
              <div className="space-y-2 text-gray-700">
                <p className="font-semibold">Configuration Validation (7 Tests):</p>
                <ul className="list-disc pl-6 space-y-1 text-xs">
                  <li><strong>KB Connection:</strong> At least 1 KB linked</li>
                  <li><strong>KB Content:</strong> Has knowledge items</li>
                  <li><strong>Coaching Notes:</strong> Has guidance notes</li>
                  <li><strong>User Feedback:</strong> Has received feedback</li>
                  <li><strong>Guard Rails:</strong> Configured (if needed)</li>
                  <li><strong>LLM Config:</strong> Has primary model</li>
                  <li><strong>Fallback Models:</strong> Has backup models</li>
                </ul>
                <p className="mt-3 font-semibold">Automated Knowledge Tests:</p>
                <p className="text-xs">Tests 3 pre-defined questions (pricing, support, services) to identify knowledge gaps before users encounter them</p>
              </div>
            </section>

            {/* Association Tab */}
            <section className="border-t pt-4">
              <h3 className="text-lg font-bold text-blue-600 mb-3">🔗 Association Tab - Embeddings</h3>
              <div className="space-y-2 text-gray-700">
                <p className="font-semibold">What are Embeddings?</p>
                <ul className="list-disc pl-6 space-y-1 text-xs">
                  <li><strong>Embeddings:</strong> 1536-dimensional vectors (arrays of numbers) representing text meaning</li>
                  <li><strong>Enable semantic search:</strong> Understand meaning, not just keywords</li>
                  <li><strong>Example:</strong> "How much?" matches "pricing" because they're semantically similar</li>
                </ul>
                <div className="bg-green-50 p-3 rounded mt-3">
                  <p className="font-semibold text-xs">How It Works:</p>
                  <ol className="list-decimal pl-6 space-y-1 text-xs mt-1">
                    <li>KB item text sent to OpenAI</li>
                    <li>OpenAI returns 1536-number array</li>
                    <li>Stored in database with item</li>
                    <li>User question becomes embedding</li>
                    <li>Similarity scores calculated</li>
                    <li>Closest matches returned</li>
                  </ol>
                </div>
                <p className="mt-3"><strong>Verification:</strong> Confirms all knowledge is searchable and AI can find relevant information</p>
              </div>
            </section>

            {/* History Tab */}
            <section className="border-t pt-4">
              <h3 className="text-lg font-bold text-blue-600 mb-3">📊 History Tab - Training Timeline</h3>
              <div className="space-y-2 text-gray-700">
                <p><strong>Time Filters:</strong> 7, 30, or 90 days</p>
                <p className="font-semibold mt-3">What It Tracks:</p>
                <ul className="list-disc pl-6 space-y-1 text-xs">
                  <li><strong>Knowledge Additions:</strong> When KB items were added</li>
                  <li><strong>Feedback Timeline:</strong> Daily positive/negative counts</li>
                  <li><strong>Conversations:</strong> Chat sessions per day</li>
                  <li><strong>Feedback Conversions:</strong> Which feedback became coaching notes</li>
                  <li><strong>Success Rate Trends:</strong> (Positive / Total) × 100</li>
                </ul>
                <div className="bg-amber-50 p-3 rounded mt-3">
                  <p className="font-semibold text-xs">Success Rate Analysis:</p>
                  <ul className="space-y-1 text-xs mt-1">
                    <li>🎉 80%+: Excellent!</li>
                    <li>✅ 60-79%: Good performance</li>
                    <li>⚠️ 40-59%: Moderate - review feedback</li>
                    <li>❌ &lt;40%: Needs improvement</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* The Training Cycle */}
            <section className="border-t pt-4">
              <h3 className="text-lg font-bold text-blue-600 mb-3">🔄 The Complete Training Cycle</h3>
              <div className="space-y-2 text-gray-700">
                <ol className="list-decimal pl-6 space-y-2 text-xs">
                  <li>
                    <strong>Setup:</strong> Create Actor → Create KB → Link via "Associated Actors" → Add content → Embeddings auto-generated
                  </li>
                  <li>
                    <strong>Initial Training:</strong> Test knowledge → Run validation → Add coaching notes → Enable guard rails
                  </li>
                  <li>
                    <strong>User Interaction:</strong> Users chat → Provide feedback → Conversations logged
                  </li>
                  <li>
                    <strong>Improvement:</strong> Review feedback → Convert to coaching → Track improvements → Monitor success rate
                  </li>
                  <li>
                    <strong>Ongoing:</strong> Check dashboard → Verify embeddings → Test scenarios → Identify trends
                  </li>
                </ol>
              </div>
            </section>

            {/* Best Practices */}
            <section className="border-t pt-4">
              <h3 className="text-lg font-bold text-blue-600 mb-3">💡 Best Practices</h3>
              <div className="space-y-2 text-gray-700">
                <ul className="list-disc pl-6 space-y-1 text-xs">
                  <li><strong>Start with KBs:</strong> Add comprehensive content BEFORE users interact</li>
                  <li><strong>Use Guard Rails:</strong> Enable for customer-facing AIs, disable for internal tools</li>
                  <li><strong>Monitor Feedback:</strong> Review negative feedback daily, convert patterns to coaching</li>
                  <li><strong>Run Validation:</strong> After adding content, after changes, monthly checks</li>
                  <li><strong>Use History:</strong> Identify when performance dropped, correlate with changes</li>
                </ul>
              </div>
            </section>

            {/* Troubleshooting */}
            <section className="border-t pt-4">
              <h3 className="text-lg font-bold text-blue-600 mb-3">🔍 Troubleshooting</h3>
              <div className="space-y-2 text-gray-700 text-xs">
                <div>
                  <p className="font-semibold">"No Knowledge Bases Linked"</p>
                  <p>Fix: Go to Knowledge Base page → Edit KB → Select actor in "Associated Actors" dropdown</p>
                </div>
                <div>
                  <p className="font-semibold">Low Similarity Scores (&lt;50%)</p>
                  <p>Fix: Query uses different words than knowledge. Add more diverse content or rephrase KB items</p>
                </div>
                <div>
                  <p className="font-semibold">No Feedback Showing</p>
                  <p>Cause: Users haven't clicked thumbs up/down yet. Wait or test yourself</p>
                </div>
              </div>
            </section>

            {/* Quick Reference */}
            <section className="border-t pt-4">
              <h3 className="text-lg font-bold text-blue-600 mb-3">📝 Quick Reference</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border p-2 text-left">Feature</th>
                      <th className="border p-2 text-left">Purpose</th>
                      <th className="border p-2 text-left">When to Use</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border p-2 font-semibold">Dashboard</td>
                      <td className="border p-2">Overview</td>
                      <td className="border p-2">Daily check-in</td>
                    </tr>
                    <tr>
                      <td className="border p-2 font-semibold">Test</td>
                      <td className="border p-2">Verify knowledge</td>
                      <td className="border p-2">Before launch, after changes</td>
                    </tr>
                    <tr>
                      <td className="border p-2 font-semibold">Validation</td>
                      <td className="border p-2">Find config issues</td>
                      <td className="border p-2">Weekly maintenance</td>
                    </tr>
                    <tr>
                      <td className="border p-2 font-semibold">Association</td>
                      <td className="border p-2">Check embeddings</td>
                      <td className="border p-2">After adding content</td>
                    </tr>
                    <tr>
                      <td className="border p-2 font-semibold">History</td>
                      <td className="border p-2">Track trends</td>
                      <td className="border p-2">Weekly/monthly review</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Summary */}
            <section className="border-t pt-4 bg-blue-50 p-4 rounded">
              <h3 className="text-lg font-bold text-blue-600 mb-3">🎓 Summary</h3>
              <div className="space-y-1 text-gray-700 text-xs">
                <p>✅ <strong>See</strong> what knowledge your AI has</p>
                <p>✅ <strong>Test</strong> how it will respond</p>
                <p>✅ <strong>Validate</strong> configuration is correct</p>
                <p>✅ <strong>Track</strong> improvements over time</p>
                <p>✅ <strong>Convert</strong> feedback into better training</p>
                <p className="mt-3 text-center font-semibold text-blue-700">Use it regularly to maintain a high-quality AI Actor! 🚀</p>
              </div>
            </section>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
